import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SimCardValidator } from '@sales/validators/async-sim-card-validator';
import { Regex } from '@shared/enums/regex';
import { IconNames } from '@shared/enums/icon-names';
import { VoiceProduct } from '@shared/models/voice-product.model';
import { SimService } from '@sales/services/sim.service';
import { SimCardType, SimCardVariant } from '@sales/models/simcard.model';
import { ErrorStateOnTouchedOrInvalidValue } from '@app/material/ErrorStateOnTouchedOrInvalidValue';
import { FormStatus } from '@sales/models/form-status.enum';
import { Store } from '@ngxs/store';
import { ReplaceSimCardAction } from '@sales/states/customer-product/customer-product.actions';
import { Subscription } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { IceButtonComponent } from '@app/shared/components/atoms/ice-button/ice-button.component';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';
import { IceInputFieldComponent } from '@app/shared/components/molecules/ice-input-field/ice-input-field.component';
import { EsimInfoModalComponent } from '../../../../../components/esim-info-modal/esim-info-modal.component';
import { IceSelectBoxComponent } from '@app/shared/components/molecules/ice-select-box/ice-select-box.component';
import { IceBackButtonComponent } from '@app/shared/components/atoms/ice-back-button/ice-back-button.component';
import { FormValidationService } from '@app/shared/services/form-validation.service';
import { AdditionalServiceVoice } from '@app/sales/modules/subscription/models/additional-service-voice.model';

@Component({
  selector: 'app-change-sim-card',
  templateUrl: './change-sim-card.component.html',
  styleUrls: ['./change-sim-card.component.scss'],
  imports: [
    IceBackButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    IceSelectBoxComponent,
    EsimInfoModalComponent,
    IceInputFieldComponent,
    SpinnerComponent,
    IceButtonComponent,
    AsyncPipe,
  ],
})
export class ChangeSimCardComponent implements OnInit, OnDestroy {
  private simService = inject(SimService);
  private store = inject(Store);

  IconNames = IconNames;
  FormStatus = FormStatus;
  SimCardType = SimCardType;
  SimCardVariant = SimCardVariant;
  getSimCardErrorTexts = FormValidationService.getSimCardErrorTexts;

  @Input() product: VoiceProduct;
  @Output() finishedChanging = new EventEmitter();

  form: FormGroup = new FormGroup({});

  hasDataSimCard: boolean;
  hasTwinSimCard: boolean;
  dataSims: AdditionalServiceVoice[] = [];
  twinSims: AdditionalServiceVoice[] = [];

  invalidValueOrTouched = new ErrorStateOnTouchedOrInvalidValue();

  subscription: Subscription;

  get simCardType(): AbstractControl {
    return this.form.get('simCardType');
  }

  get simCardVariant(): AbstractControl {
    return this.form.get('simCardVariant');
  }

  get newIcc(): AbstractControl {
    return this.form.get('newIcc');
  }

  get replacedSim(): AbstractControl {
    return this.form.get('replacedSim');
  }

  get simsOfSelectedType(): AdditionalServiceVoice[] {
    if (this.simCardType.value === SimCardType.Data) return this.dataSims;
    if (this.simCardType.value === SimCardType.Twin) return this.twinSims;
    return [];
  }

  get replacedSimOptions(): { aNumber: string; icc: string; label: string }[] {
    const simName = this.simCardType.value === SimCardType.Data ? 'Data-SIM' : 'Tvilling-SIM';
    return this.simsOfSelectedType.map((sim, index) => ({
      aNumber: sim.aNumber,
      icc: sim.icc,
      label: `${simName} ${index + 1}`,
    }));
  }

  get replacedSimANumber(): string {
    return this.replacedSim.value ?? this.simsOfSelectedType[0]?.aNumber;
  }

  get isSubmitDisabled(): boolean {
    if (this.simCardVariant?.value === SimCardVariant.ESim) return false;

    const isSameReplacedSim = this.replacedSimANumber === this.product.replaceAdditionalSimANumber;

    return (
      this.newIcc.pending ||
      this.newIcc.status !== 'VALID' ||
      (this.simsOfSelectedType.length > 1 && !this.replacedSim.value) ||
      (this.simCardType.value === SimCardType.Voice && this.newIcc.value === this.product.replaceVoiceSimIcc) ||
      (this.simCardType.value === SimCardType.Data &&
        this.newIcc.value === this.product.replaceDataSimIcc &&
        isSameReplacedSim) ||
      (this.simCardType.value === SimCardType.Twin &&
        this.newIcc.value === this.product.replaceTwinSimIcc &&
        isSameReplacedSim)
    );
  }

  get displayESim(): boolean {
    return this.simCardType.value === SimCardType.Voice;
  }

  ngOnInit() {
    this.form = this.buildForm();
    this.subscription = this.form.controls.simCardType.valueChanges.subscribe(type => {
      if (!this.newIcc.value) this.newIcc.reset();
      this.replacedSim.setValue(null);
      if (type !== SimCardType.Voice) {
        this.simCardVariant.setValue(SimCardVariant.Physical);
      } else {
        this.simCardVariant.setValue(null);
      }
    });

    if (!this.product.subscriptionServices) {
      return;
    }

    this.dataSims = this.product.additionalServices.filter(s => s.isDataSimCard && s.aNumber !== null);
    this.twinSims = this.product.additionalServices.filter(s => s.isTwinSimCard && s.aNumber !== null);
    this.hasDataSimCard = this.dataSims.length > 0;
    this.hasTwinSimCard = this.twinSims.length > 0;
  }

  private buildForm(): FormGroup {
    return new FormGroup({
      simCardType: new FormControl(this.getSimCardTypeFromProduct(this.product), [Validators.required]),
      simCardVariant: new FormControl(this.getSimCardVariantFromProduct(this.product), [Validators.required]),
      newIcc: new FormControl(
        this.getSimCardIccValueFromProduct(this.product),
        [Validators.required, Validators.pattern(Regex.ICC), Validators.minLength(19), Validators.maxLength(20)],
        [SimCardValidator(this.simService, SimCardType.Voice)]
      ),
      replacedSim: new FormControl<string>(this.product.replaceAdditionalSimANumber ?? null),
    });
  }

  private getSimCardTypeFromProduct(product: VoiceProduct): SimCardType {
    const { replaceVoiceSimIcc, replaceDataSimIcc, replaceTwinSimIcc, replaceVoiceESim } = product;

    if (!!replaceVoiceSimIcc || replaceVoiceESim) {
      return SimCardType.Voice;
    } else if (!!replaceDataSimIcc) {
      return SimCardType.Data;
    } else if (!!replaceTwinSimIcc) {
      return SimCardType.Twin;
    }

    return SimCardType.Unknown;
  }

  private getSimCardVariantFromProduct(product: VoiceProduct): SimCardVariant {
    const { replaceVoiceSimIcc, replaceDataSimIcc, replaceTwinSimIcc, replaceVoiceESim } = product;

    if (!!replaceVoiceSimIcc || !!replaceDataSimIcc || !!replaceTwinSimIcc) {
      return SimCardVariant.Physical;
    } else if (replaceVoiceESim) {
      return SimCardVariant.ESim;
    }

    return SimCardVariant.Unknown;
  }

  private getSimCardIccValueFromProduct(product: VoiceProduct): string {
    const { replaceVoiceSimIcc, replaceDataSimIcc, replaceTwinSimIcc } = product;
    return replaceVoiceSimIcc || replaceDataSimIcc || replaceTwinSimIcc || '';
  }

  onSave(): void {
    this.store.dispatch(
      new ReplaceSimCardAction(
        this.product.id,
        this.newIcc.value,
        this.simCardType.value,
        this.simCardVariant.value,
        this.replacedSimANumber
      )
    );
    this.finishedChanging.emit();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
